import React, { useState, useEffect, useRef, useMemo } from 'react';
import lumiObserver from '../lumiObserver';

// ── RBA Monetary Policy lab — HSC Economics / Business
// Student adjusts the cash rate to steer inflation back into the 2–3% band
// while avoiding a spike in unemployment. Inflation responds with ~6-month
// lag; unemployment with ~12-month lag. The model is a simple AR(1) nudge
// toward a steady state determined by the rate.
// ────────────────────────────────────────────────────────────────────────

const T = {
  bg: '#0D1117', surface: '#161B22', border: '#30363D',
  text: '#E6EDF3', textMid: '#8B949E',
  accent: '#0B8A7E', correct: '#10B981', wrong: '#EF4444', hint: '#F59E0B',
};

// 36 monthly steps (3 years)
const MONTHS = 36;

export default function MonetaryPolicy({ interactiveId = 'monetary_policy_p1' }) {
  const [cashRate, setCashRate] = useState(4.1);            // RBA cash rate %
  const [month, setMonth] = useState(0);
  const [series, setSeries] = useState(() => ({
    inflation:    [4.2],       // starting state: above band
    unemployment: [4.0],
    rate:         [4.1],
  }));
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  // Steady-state targets given rate: higher rate cools inflation (lag 6mo)
  // and raises unemployment (lag 12mo).
  const targetInflationFor = (r) => Math.max(0, 7 - 1.1 * r);      // rough
  const targetUnempFor      = (r) => Math.max(3.2, 2.5 + 0.5 * r);  // rough

  const tick = () => {
    setMonth(m => {
      if (m >= MONTHS) { setRunning(false); return m; }
      return m + 1;
    });
  };

  // Advance one month of dynamics when month changes.
  useEffect(() => {
    if (month === 0) return;
    setSeries(prev => {
      const lastInf = prev.inflation[prev.inflation.length - 1];
      const lastUn  = prev.unemployment[prev.unemployment.length - 1];
      const invTarget = targetInflationFor(cashRate);
      const unTarget  = targetUnempFor(cashRate);
      // 6-month lag → adjustment speed ~ 1/6 per month toward target; add noise
      const noise = () => (Math.random() - 0.5) * 0.15;
      const nextInf = lastInf + (invTarget - lastInf) * (1 / 6) + noise();
      const nextUn  = lastUn  + (unTarget  - lastUn)  * (1 / 12) + noise();
      return {
        inflation:    [...prev.inflation, Number(nextInf.toFixed(2))],
        unemployment: [...prev.unemployment, Number(nextUn.toFixed(2))],
        rate:         [...prev.rate, cashRate],
      };
    });
  // eslint-disable-next-line
  }, [month]);

  // Auto-play
  useEffect(() => {
    if (!running) { if (timerRef.current) clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(tick, 220);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const emit = (type, payload) => lumiObserver.emit({ interactiveId, type, payload });

  const onRateChange = (prev, next) => {
    setCashRate(next);
    emit('rate_changed', { from: prev, to: next, month });
  };

  // On every month advance, check target band
  useEffect(() => {
    if (month === 0 || month >= MONTHS) return;
    const inf = series.inflation[series.inflation.length - 1];
    const un  = series.unemployment[series.unemployment.length - 1];
    if (inf >= 2 && inf <= 3) emit('target_hit', { month, inflation: inf });
    if (un > 6.0) emit('misconception_flag', { kind: 'over_tightening', unemployment: un, month });
  // eslint-disable-next-line
  }, [month]);

  const reset = () => {
    setMonth(0); setRunning(false);
    setSeries({ inflation: [4.2], unemployment: [4.0], rate: [cashRate] });
  };

  // Rendering — line chart over months for three series
  const W = 420, H = 180, pad = 30;
  const xFor = (i) => pad + (i / MONTHS) * (W - 2 * pad);
  const yFor = (v, min, max) => pad + (1 - (v - min) / (max - min)) * (H - 2 * pad);

  const path = (arr, min, max, color) => {
    const pts = arr.map((v, i) => `${xFor(i).toFixed(1)},${yFor(v, min, max).toFixed(1)}`).join(' ');
    return <polyline points={pts} fill="none" stroke={color} strokeWidth={2} />;
  };

  const latest = useMemo(() => ({
    inf: series.inflation[series.inflation.length - 1],
    un:  series.unemployment[series.unemployment.length - 1],
  }), [series]);

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ fontSize: 11, color: T.textMid, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>Economics · HSC · Monetary Policy</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>RBA Cash Rate Lab</div>
      <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>
        Set the cash rate, press Play, and steer inflation back into the 2–3% band over 3 years — without pushing unemployment above 6%.
      </div>

      {/* Chart */}
      <svg width={W} height={H} style={{ display: 'block', margin: '12px auto', background: T.bg, borderRadius: 8 }} role="img" aria-label="Inflation and unemployment over time">
        {/* Target band (inflation 2-3%) */}
        <rect x={pad} y={yFor(3, 0, 8)} width={W - 2 * pad} height={yFor(2, 0, 8) - yFor(3, 0, 8)} fill={T.correct + '22'} />
        {/* Axes */}
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke={T.border} />
        <line x1={pad} y1={pad}     x2={pad}     y2={H - pad} stroke={T.border} />
        {/* Series */}
        {path(series.inflation,    0, 8, T.hint)}
        {path(series.unemployment, 2, 10, T.accent)}
        {path(series.rate,         0, 12, T.textMid)}
        {/* Month cursor */}
        <line x1={xFor(month)} y1={pad} x2={xFor(month)} y2={H - pad} stroke={T.textMid} strokeDasharray="3 3" />
      </svg>

      {/* Legend + readout */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 12, color: T.textMid, marginBottom: 8 }}>
        <span><span style={{ color: T.hint, fontWeight: 800 }}>— Inflation</span> {latest.inf.toFixed(2)}%</span>
        <span><span style={{ color: T.accent, fontWeight: 800 }}>— Unemployment</span> {latest.un.toFixed(2)}%</span>
        <span><span style={{ color: T.textMid, fontWeight: 800 }}>— Cash rate</span> {cashRate.toFixed(2)}%</span>
      </div>

      {/* Controls */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: T.textMid }}>
          <span>Cash rate</span><span style={{ color: T.accent }}>{cashRate.toFixed(2)}%</span>
        </div>
        <input type="range" min={0} max={10} step={0.25} value={cashRate}
          onChange={e => onRateChange(cashRate, Number(e.target.value))}
          style={{ width: '100%', accentColor: T.accent }} aria-label="Cash rate" />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'center' }}>
        <button onClick={() => setRunning(r => !r)} style={{ padding: '8px 16px', background: running ? T.hint : T.correct, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          {running ? 'Pause' : 'Play'}
        </button>
        <button onClick={reset} style={{ padding: '8px 14px', background: T.bg, color: T.textMid, border: `1px solid ${T.border}`, borderRadius: 8, cursor: 'pointer' }}>
          Reset
        </button>
        <div style={{ alignSelf: 'center', fontSize: 12, color: T.textMid }}>Month {month} / {MONTHS}</div>
      </div>
    </div>
  );
}
